import { useState, useEffect, useMemo } from "react";
import {
  Box, Button, Chip, CircularProgress, Divider,
  IconButton, Stack, Tooltip, Typography,
} from "@mui/material";
import SearchIcon       from "@mui/icons-material/Search";
import FiberNewIcon     from "@mui/icons-material/FiberNew";
import CableIcon        from "@mui/icons-material/Cable";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HelpOutlineIcon  from "@mui/icons-material/HelpOutline";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs }         from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker }            from "@mui/x-date-pickers/DatePicker";
import { DataGridPro }           from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import API from "../../../../api";
import { colors, dataGridStyle } from "../../../../theme";
import {
  mapAssetRows, makeTagColor, vulnSeverityTier, severityStyleForTier, severityIconForTier,
  worstVulnerability, allVulnerabilitiesForAsset, hostnameAlternatives, osIcon,
} from "./assetUtils";
import { resolveClassification, classificationKey } from "./assetClassification";
import AssetRiskChip from "./AssetRiskChip";
import AssetSidePanel from "./AssetSidePanel";
import IpHistoryDialog from "./IpHistoryDialog";

// ── helpers ───────────────────────────────────────────────────────────────────

const parseDate = (v) => {
  if (!v) return null;
  const raw = v?.$date ?? v;
  const d = dayjs(raw);
  return d.isValid() ? d : null;
};
const fmtDate = (v) => parseDate(v)?.format("YYYY-MM-DD HH:mm:ss") ?? "—";

// Renders up to `max` service chips — same style as HostsOnlineGrid. `services`
// carries only exposed server-side ports now, so there is no Server/Client
// distinction left to draw; the tags are what's worth surfacing instead.
const ServiceChips = ({ services, max = 5, tagColor, highlightNew = false }) => {
  const list = (services || []).slice().sort((a, b) => (a.port ?? 0) - (b.port ?? 0));
  if (list.length === 0) return <Typography sx={{ color: "#555", fontSize: 12 }}>—</Typography>;
  const visible = list.slice(0, max);
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4, py: 0.5 }}>
      {visible.map((s, i) => {
        const port  = s.port?.$numberLong != null ? Number(s.port.$numberLong) : s.port;
        const proto = s.protocol ? `/${s.protocol.toLowerCase()}` : "";
        const name  = s.name ?? s.service;
        const tags  = s.tags || [];
        const label = [port != null ? `${port}${proto}` : null, name].filter(Boolean).join(" · ");
        // A tagged port is the interesting kind — colour it by its first tag so the
        // attack-surface signal survives even in the compressed chip view.
        const tagStyle = tags.length > 0 && tagColor ? tagColor(tags[0]) : null;
        const chipSx = highlightNew
          ? { bgcolor: "#4cceac22", color: "#4cceac", fontSize: 10, height: 18, fontWeight: 600 }
          : tagStyle
            ? { bgcolor: tagStyle.bg, color: tagStyle.color, fontSize: 10, height: 18, fontWeight: 500 }
            : { bgcolor: "#ffffff11", color: "#ccc", fontSize: 10, height: 18 };
        return (
          <Tooltip key={i} title={tags.join(", ")}>
            <Chip label={label} size="small" sx={chipSx} />
          </Tooltip>
        );
      })}
      {list.length > max && (
        <Chip
          label={`+${list.length - max}`}
          size="small"
          sx={{ bgcolor: "#ffffff11", color: "#888", fontSize: 10, height: 18 }}
        />
      )}
    </Box>
  );
};

// ── presets ────────────────────────────────────────────────────────────────────

const PRESETS = [
  { label: "24h", days: 1  },
  { label: "3d",  days: 3  },
  { label: "7d",  days: 7  },
  { label: "15d", days: 15 },
  { label: "30d", days: 30 },
];

// ── section header ─────────────────────────────────────────────────────────────

const ResultSection = ({ icon: Icon, title, count, color, children }) => (
  <Box sx={{ mb: 4 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      <Icon sx={{ color, fontSize: 22 }} />
      <Typography sx={{ fontWeight: 700, fontSize: 16, color }}>{title}</Typography>
      <Chip
        label={count}
        size="small"
        sx={{ bgcolor: `${color}22`, color, fontWeight: 700, fontSize: 12, height: 22 }}
      />
    </Box>
    {children}
  </Box>
);

const EmptyState = ({ label }) => (
  <Box
    sx={{
      py: 5, textAlign: "center",
      border: "1px solid rgba(255,255,255,0.06)",
      borderRadius: 1,
      color: "#555",
      fontSize: 13,
      fontStyle: "italic",
    }}
  >
    {label}
  </Box>
);

// ── shared column builders ─────────────────────────────────────────────────────

const infoCol = (setSidePanelRow) => ({
  field: "__info", headerName: "", width: 44,
  sortable: false, filterable: false, disableColumnMenu: true, hideable: false,
  align: "center", headerAlign: "center",
  renderCell: (p) => (
    <Tooltip title="View asset details">
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); setSidePanelRow(p.row); }}
        sx={{ color: colors.grey[100], "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" } }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Tooltip>
  ),
});

const statusCol = {
  field: "status", headerName: "STATUS", width: 90,
  align: "center", headerAlign: "center",
  renderCell: (p) => {
    if (p.value == null)
      return <Tooltip title="Unknown"><HelpOutlineIcon sx={{ color: "#777" }} /></Tooltip>;
    const online = p.value === "online";
    return (
      <Chip
        label={p.value.toUpperCase()}
        size="small"
        sx={{
          bgcolor: online ? "#4cceac22" : "#ff444422",
          color:   online ? "#4cceac"   : "#ff6644",
          fontSize: 10, height: 20, fontWeight: 600,
        }}
      />
    );
  },
};

const ipCol = {
  field: "ip", headerName: "IP ADDRESS", width: 145,
  renderCell: (p) => <Box sx={{ fontFamily: "monospace", fontWeight: 600 }}>{p.value}</Box>,
};

const vlanCol = {
  field: "vlan", headerName: "VLAN", width: 68, align: "center", headerAlign: "center",
  renderCell: (p) =>
    p.value != null
      ? <Chip label={p.value} size="small" variant="outlined" sx={{ fontWeight: "bold", fontSize: 11 }} />
      : <Typography sx={{ color: "#555", fontSize: 12 }}>—</Typography>,
};

const vendorCol = {
  field: "vendor", headerName: "VENDOR", width: 150,
  renderCell: (p) => {
    if (!p.value) return <Typography sx={{ color: "#555", fontSize: 12 }}>—</Typography>;
    const src = p.row.vendor_source;
    const explain =
      src === "oui" ? "Inferred from the MAC address prefix"
        : src === "device-identity" ? "Declared by the device itself — more reliable than the OUI lookup"
          : src ? `Source: ${src}` : "";
    return <Tooltip title={explain}><Typography sx={{ fontSize: 12.5 }} noWrap>{p.value}</Typography></Tooltip>;
  },
};

// Shape from the most specific classification level present, in the neutral
// foreground colour — same as the Inventory grid, including the one exception:
// `is_gateway` tints the glyph amber, because a gateway's observed traffic is not
// necessarily its own and that reframes every other column on the row.
const typeCol = {
  field: "device_type", headerName: "TYPE", width: 58,
  sortable: true, align: "center", headerAlign: "center",
  valueGetter: (_, row) => classificationKey(row.classification),
  renderCell: (p) => {
    const k = resolveClassification(p.row.classification);
    const Icon = k.icon;
    const gateway = p.row.is_gateway === true;
    const title = gateway
      ? `${k.path || "Not classified"} · Gateway — routes for other hosts, so its observed traffic is not necessarily its own`
      : (k.path || "Not classified");
    return (
      <Tooltip title={title}>
        <Icon style={{ fontSize: 20, color: gateway ? "#ffb422" : colors.grey[100] }} />
      </Tooltip>
    );
  },
};

const hostnameCol = {
  field: "hostname", headerName: "HOSTNAME", width: 175,
  renderCell: (p) => {
    if (!p.value) return <Typography sx={{ color: "#555", fontSize: 12 }}>—</Typography>;
    const alternatives = hostnameAlternatives(p.row.host_names, p.row.hostname_source);
    const title = [
      p.row.hostname_source_label ? `Chosen from ${p.row.hostname_source_label}` : null,
      ...alternatives.map((a) => `${a.label}: ${a.name}`),
    ].filter(Boolean).join(" · ");
    return (
      <Tooltip title={title}>
        <Typography sx={{ fontSize: 12, fontFamily: "monospace" }} noWrap>{p.value}</Typography>
      </Tooltip>
    );
  },
};

// Family on top, commercial release underneath — `os.version` is the release name,
// `os.build` the build number. The tooltip carries the confidence for each level so
// a 25-confidence guess never reads like a 90-confidence fact.
const osCol = {
  field: "os_family", headerName: "OS", minWidth: 190,
  valueGetter: (_, row) => row.os?.family || "",
  renderCell: (p) => {
    const os = p.row.os;
    if (!os?.family && !os?.version) return <Typography sx={{ color: "#555", fontSize: 12 }}>—</Typography>;
    const Icon = osIcon(os);
    const title = (
      <Box>
        {os.family && <div>Family: {os.family}{os.family_confidence != null ? ` · confidence ${os.family_confidence}` : ""}</div>}
        {os.version && <div>Release: {os.version}{os.version_confidence != null ? ` · confidence ${os.version_confidence}` : ""}</div>}
        {os.build && <div>Build: {os.build}{os.build_confidence != null ? ` · confidence ${os.build_confidence}` : ""}</div>}
      </Box>
    );
    return (
      <Tooltip title={title}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, color: colors.grey[200] }}>
          {Icon
            ? <Icon style={{ fontSize: 19, flexShrink: 0 }} />
            : <HelpOutlineIcon sx={{ color: colors.grey[500], fontSize: 17, flexShrink: 0 }} />}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 12.5, lineHeight: 1.2 }} noWrap>{os.family || os.version}</Typography>
            {os.family && os.version && (
              <Typography sx={{ fontSize: 10, color: colors.grey[500], lineHeight: 1.2 }} noWrap>
                {os.version}
              </Typography>
            )}
          </Box>
        </Box>
      </Tooltip>
    );
  },
};

// Band label only — same treatment as the Inventory grid; the number and the
// factor breakdown live in the tooltip and on the asset's Security tab.
const riskScoreCol = {
  field: "risk_score", headerName: "RISK", width: 110,
  align: "center", headerAlign: "center",
  renderCell: (p) =>
    typeof p.value === "number"
      ? <AssetRiskChip score={p.value} factors={p.row.risk_factors} />
      : <Typography sx={{ color: "#555", fontSize: 12 }}>—</Typography>,
};

const cveCol = {
  field: "vulnerabilities", headerName: "CVES", minWidth: 180,
  valueGetter: (_, row) => allVulnerabilitiesForAsset(row),
  sortComparator: (v1, v2) => {
    const rank = (v) => ({ critical: 4, high: 3, medium: 2, low: 1, none: 0 }[vulnSeverityTier(worstVulnerability(v))] ?? 0);
    return rank(v1) - rank(v2);
  },
  renderCell: (p) => {
    const vulns = p.value || [];
    if (vulns.length === 0) return <Typography sx={{ color: "#555", fontSize: 12 }}>—</Typography>;
    const worst = worstVulnerability(vulns);
    const tier = vulnSeverityTier(worst);
    const c = severityStyleForTier(tier);
    const Icon = severityIconForTier(tier);
    return (
      <Tooltip title={`Worst: ${worst?.cve_id ?? "—"}${worst?.matched_on ? ` (matched on ${worst.matched_on})` : ""}`}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, py: 0.5 }}>
          {Icon && <Icon sx={{ color: c.color, fontSize: 22, flexShrink: 0 }} />}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: c.color, lineHeight: 1.2 }}>
              {c.label.toUpperCase()}
            </Typography>
            <Typography sx={{ fontSize: 10.5, color: colors.grey[500], lineHeight: 1.2 }}>
              {vulns.length} CVE{vulns.length > 1 ? "s" : ""}
            </Typography>
          </Box>
        </Box>
      </Tooltip>
    );
  },
};

const tagsCol = (tagColor) => ({
  field: "allTags", headerName: "TAGS", width: 200, sortable: false,
  renderCell: (p) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4, py: 0.5 }}>
      {(p.value || []).map((tag, i) => {
        const c = tagColor(tag);
        return <Chip key={i} label={tag} size="small" sx={{ bgcolor: c.bg, color: c.color, fontSize: 10, height: 18, fontWeight: 500 }} />;
      })}
    </Box>
  ),
});

const lastSeenCol = {
  field: "last_seen", headerName: "LAST SEEN", width: 165,
  renderCell: (p) => <Typography sx={{ fontSize: 12 }}>{fmtDate(p.value)}</Typography>,
};

// ── main component ─────────────────────────────────────────────────────────────

const NewDiscoveries = () => {
  const [dateFrom, setDateFrom] = useState(() => dayjs().subtract(7, "day"));
  const [dateTo,   setDateTo]   = useState(() => dayjs());
  const [loading,  setLoading]  = useState(false);
  const [result,   setResult]   = useState(null);
  const [sidePanelRow,      setSidePanelRow]      = useState(null);
  const [ipHistoryRow,      setIpHistoryRow]      = useState(null);
  const [tagColorMap,       setTagColorMap]        = useState({});

  useEffect(() => {
    API.getSystemConfig("ot", "port_tags").then((res) => {
      const c = res.data?.[0]?.value?.tag_colors ?? {};
      setTagColorMap(c);
    }).catch(() => {});
  }, []);

  const tagColor = useMemo(() => makeTagColor(tagColorMap), [tagColorMap]);

  const applyPreset = (days) => {
    setDateFrom(dayjs().subtract(days, "day"));
    setDateTo(dayjs());
  };

  const handleSearch = () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    API.getNewDiscoveries({
      from: dateFrom.startOf("day").toISOString(),
      to:   dateTo.endOf("day").toISOString(),
    })
      .then((res) => setResult(res.data))
      .catch(() => setResult({ success: false }))
      .finally(() => setLoading(false));
  };

  const handleRowClick = (params, event) => {
    if (event.target?.closest?.("button, a, input, .MuiIconButton-root")) return;
    setSidePanelRow(params.row);
  };

  // ── columns: new hosts ───────────────────────────────────────────────────────

  const newHostCols = useMemo(() => [
    infoCol(setSidePanelRow),
    // status -> identity -> security, then the columns that make this view what it
    // is: when the host showed up and what it already exposes.
    statusCol,
    lastSeenCol,
    typeCol,
    hostnameCol,
    ipCol,
    vendorCol,
    osCol,
    vlanCol,
    riskScoreCol,
    cveCol,
    {
      field: "services", headerName: "EXPOSED PORTS", width: 320, sortable: false,
      renderCell: (p) => <ServiceChips services={p.value} tagColor={tagColor} />,
    },
    {
      field: "first_seen", headerName: "FIRST SEEN", width: 165,
      renderCell: (p) => (
        <Tooltip title="Discovery date — this host appeared for the first time in the selected range">
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#7de8ed" }}>{fmtDate(p.value)}</Typography>
        </Tooltip>
      ),
    },
  ], [tagColor]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── columns: hosts with new services ────────────────────────────────────────

  const newSvcCols = useMemo(() => [
    infoCol(setSidePanelRow),
    statusCol,
    lastSeenCol,
    typeCol,
    hostnameCol,
    ipCol,
    vendorCol,
    osCol,
    vlanCol,
    riskScoreCol,
    cveCol,
    {
      field: "newServices", headerName: "NEWLY EXPOSED", width: 300, sortable: false,
      renderCell: (p) => <ServiceChips services={p.value} tagColor={tagColor} highlightNew />,
    },
    {
      field: "services", headerName: "ALL EXPOSED PORTS", width: 260, sortable: false,
      renderCell: (p) => <ServiceChips services={p.value} tagColor={tagColor} />,
    },
    tagsCol(tagColor),
    {
      field: "first_seen", headerName: "HOST FIRST SEEN", width: 165,
      renderCell: (p) => (
        <Tooltip title="This host was already known before the selected range">
          <Typography sx={{ fontSize: 12, color: "#888" }}>{fmtDate(p.value)}</Typography>
        </Tooltip>
      ),
    },
  ], [tagColor]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── rows ──────────────────────────────────────────────────────────────────────

  const newHostRows = useMemo(() => mapAssetRows(result?.newHosts || []), [result]);

  const newSvcRows = useMemo(() => {
    const hosts = result?.hostsWithNewServices || [];
    return mapAssetRows(hosts).map((row, i) => ({ ...row, newServices: hosts[i]?.newServices ?? [] }));
  }, [result]);

  const searched = result !== null;

  const gridSx = {
    ...dataGridStyle,
    "& .MuiDataGrid-row": { cursor: "pointer" },
  };

  // ── render ────────────────────────────────────────────────────────────────────

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {/* date range bar */}
        <Box
          sx={{
            display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
            mb: 3, p: 2,
            backgroundColor: "rgba(69,144,149,0.05)",
            border: "1px solid rgba(69,144,149,0.15)",
            borderRadius: 2,
          }}
        >
          <Stack direction="row" spacing={0.75}>
            {PRESETS.map((p) => (
              <Chip
                key={p.label}
                label={p.label}
                size="small"
                variant="outlined"
                onClick={() => applyPreset(p.days)}
                sx={{
                  cursor: "pointer",
                  borderColor: "rgba(69,144,149,0.4)",
                  color: "#449095",
                  fontSize: 11,
                  "&:hover": { backgroundColor: "rgba(69,144,149,0.12)" },
                }}
              />
            ))}
          </Stack>

          <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

          <DatePicker
            label="From"
            value={dateFrom}
            onChange={(v) => v && setDateFrom(v)}
            slotProps={{
              textField: {
                size: "small",
                sx: { width: 155, "& .MuiOutlinedInput-root": { fontSize: "0.82rem" }, "& .MuiInputLabel-root": { fontSize: "0.82rem" } },
              },
            }}
          />
          <DatePicker
            label="To"
            value={dateTo}
            minDate={dateFrom ?? undefined}
            onChange={(v) => v && setDateTo(v)}
            slotProps={{
              textField: {
                size: "small",
                sx: { width: 155, "& .MuiOutlinedInput-root": { fontSize: "0.82rem" }, "& .MuiInputLabel-root": { fontSize: "0.82rem" } },
              },
            }}
          />

          <Button
            variant="contained"
            size="small"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
            disabled={loading || !dateFrom || !dateTo}
            onClick={handleSearch}
            sx={{
              backgroundColor: "#449095", color: "#fff", fontWeight: 700, px: 3,
              "&:hover": { backgroundColor: "#3a7a7e" },
              "&:disabled": { backgroundColor: "rgba(69,144,149,0.3)" },
            }}
          >
            {loading ? "Searching…" : "Search"}
          </Button>
        </Box>

        {/* placeholder */}
        {!searched && !loading && (
          <Box sx={{ py: 6, textAlign: "center", color: "#555", fontSize: 14 }}>
            Select a date range and click Search to find new discoveries.
          </Box>
        )}

        {/* results */}
        {searched && !loading && (
          <>
            <ResultSection icon={FiberNewIcon} title="New Hosts" count={newHostRows.length} color="#7de8ed">
              {newHostRows.length === 0 ? (
                <EmptyState label="No new hosts found in this range." />
              ) : (
                <Box sx={{ height: Math.min(600, 56 + newHostRows.length * 60), width: "100%" }}>
                  <DataGridPro
                    rows={newHostRows}
                    columns={newHostCols}
                    density="compact"
                    disableRowSelectionOnClick
                    hideFooterSelectedRowCount
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                    pagination
                    getRowHeight={() => "auto"}
                    onRowClick={handleRowClick}
                    sx={gridSx}
                  />
                </Box>
              )}
            </ResultSection>

            <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.07)" }} />

            <ResultSection icon={CableIcon} title="New Services on Known Hosts" count={newSvcRows.length} color="#ce93d8">
              {newSvcRows.length === 0 ? (
                <EmptyState label="No new services found on existing hosts in this range." />
              ) : (
                <Box sx={{ height: Math.min(600, 56 + newSvcRows.length * 60), width: "100%" }}>
                  <DataGridPro
                    rows={newSvcRows}
                    columns={newSvcCols}
                    density="compact"
                    disableRowSelectionOnClick
                    hideFooterSelectedRowCount
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                    pagination
                    getRowHeight={() => "auto"}
                    onRowClick={handleRowClick}
                    sx={gridSx}
                  />
                </Box>
              )}
            </ResultSection>
          </>
        )}
      </Box>

      <AssetSidePanel
        row={sidePanelRow}
        open={Boolean(sidePanelRow)}
        onClose={() => setSidePanelRow(null)}
        onShowIpHistory={setIpHistoryRow}
        tagColor={tagColor}
      />
      <IpHistoryDialog
        row={ipHistoryRow}
        onClose={() => setIpHistoryRow(null)}
      />
    </LocalizationProvider>
  );
};

export default NewDiscoveries;
